import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface PatternData {
  grid: number[][];
  colors: Array<{
    index: number;
    rgb: number[];
    hex: string;
    name?: string;
  }>;
  dimensions: {
    width: number;
    height: number;
  };
  scanlines: any[];
  yarn_requirements: any;
  gauge_info: any;
  yarn_weight: number;
  difficulty: string;
  estimated_time: any;
  initial_palette?: Array<{
    index: number;
    rgb: number[];
    hex: string;
    name?: string;
  }>;
}

interface PatternPreviewProps {
  patternData: PatternData;
  onPatternUpdate?: (newPatternData: PatternData) => void;
  uploadConfig?: any; // Store the original upload configuration
}

const PatternPreview: React.FC<PatternPreviewProps> = ({ patternData, onPatternUpdate }) => {
  const [activeTab, setActiveTab] = useState<'grid' | 'materials' | 'instructions'>('grid');
  const [downloading, setDownloading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedColor, setSelectedColor] = useState(0);
  const [editedGrid, setEditedGrid] = useState<number[][]>(patternData.grid);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [completedRows, setCompletedRows] = useState<Set<number>>(new Set());
  const [backgroundColorIndex, setBackgroundColorIndex] = useState<number | undefined>(undefined);
  const [regeneratingPattern, setRegeneratingPattern] = useState(false);
  const [displayColors, setDisplayColors] = useState(patternData.colors);
  const [hasUnassignedPadding, setHasUnassignedPadding] = useState(false);
  const [updatedScanlines, setUpdatedScanlines] = useState(patternData.scanlines);

  useEffect(() => {
    setEditedGrid(patternData.grid);
    setCompletedRows(new Set()); // Reset completion tracking for new pattern
    setDisplayColors(patternData.colors);
    setBackgroundColorIndex(undefined);
    setUpdatedScanlines(patternData.scanlines);
    
    // Check if there are unassigned padding areas (-1 values)
    const hasUnassigned = patternData.grid.some(row => row.some(cell => cell === -1));
    setHasUnassignedPadding(hasUnassigned);
  }, [patternData.grid, patternData.colors, patternData.scanlines]);

  const handleRowCompletion = (rowNumber: number) => {
    const newCompletedRows = new Set(completedRows);
    if (newCompletedRows.has(rowNumber)) {
      newCompletedRows.delete(rowNumber);
    } else {
      newCompletedRows.add(rowNumber);
    }
    setCompletedRows(newCompletedRows);
  };

  const handlePixelClick = (rowIndex: number, colIndex: number) => {
    if (!editMode) return;
    
    const newGrid = editedGrid.map((row, rIndex) => 
      rIndex === rowIndex 
        ? row.map((cell, cIndex) => cIndex === colIndex ? selectedColor : cell)
        : row
    );
    setEditedGrid(newGrid);
    console.log(`Changed cell [${rowIndex}, ${colIndex}] to color ${selectedColor}`);
  };

  const handleBackgroundColorChange = (colorIndex: number | undefined) => {
    setBackgroundColorIndex(colorIndex);
    
    if (colorIndex !== undefined && patternData.initial_palette) {
      // Apply background color change to the pattern grid
      const selectedColor = patternData.initial_palette[colorIndex];
      updatePatternWithBackgroundColor(selectedColor);
      console.log('Applied background color:', selectedColor.hex);
    } else {
      // Reset to original pattern
      setEditedGrid(patternData.grid);
      setDisplayColors(patternData.colors);
    }
  };

  const regenerateScanlines = (grid: number[][], colors: any[]) => {
    const newScanlines = [];
    const height = grid.length;
    const width = grid[0].length;
    
    for (let row_idx = 0; row_idx < height; row_idx++) {
      const row = grid[row_idx];
      const is_reverse = row_idx % 2 === 1;
      const processedRow = is_reverse ? [...row].reverse() : row;
      
      // Count consecutive stitches by color
      const instructions = [];
      let current_color = processedRow[0];
      let count = 1;
      
      for (let col_idx = 1; col_idx < processedRow.length; col_idx++) {
        if (processedRow[col_idx] === current_color) {
          count += 1;
        } else {
          // Add instruction for current color group
          const color = colors[current_color];
          instructions.push({
            count: count,
            color_index: current_color,
            color_name: color ? `${String.fromCharCode(65 + current_color)} (${color.name || 'Unknown'})` : 'Unknown',
            color_hex: color ? color.hex : '#000000'
          });
          
          current_color = processedRow[col_idx];
          count = 1;
        }
      }
      
      // Add the last group
      const color = colors[current_color];
      instructions.push({
        count: count,
        color_index: current_color,
        color_name: color ? `${String.fromCharCode(65 + current_color)} (${color.name || 'Unknown'})` : 'Unknown',
        color_hex: color ? color.hex : '#000000'
      });
      
      newScanlines.push({
        row: row_idx + 1,
        direction: is_reverse ? 'right-to-left' : 'left-to-right',
        instructions: instructions
      });
    }
    
    return newScanlines;
  };

  const updatePatternWithBackgroundColor = (selectedColor: { index: number; rgb: number[]; hex: string; name?: string; }) => {
    const newGrid = editedGrid.map(row => [...row]);
    
    // Find the closest matching color in the existing pattern colors
    let targetColorIndex = 0;
    let minDistance = Infinity;
    
    patternData.colors.forEach((color, index) => {
      const distance = Math.sqrt(
        Math.pow(selectedColor.rgb[0] - color.rgb[0], 2) +
        Math.pow(selectedColor.rgb[1] - color.rgb[1], 2) +
        Math.pow(selectedColor.rgb[2] - color.rgb[2], 2)
      );
      if (distance < minDistance) {
        minDistance = distance;
        targetColorIndex = index;
      }
    });
    
    // If no close match found (distance > 100), add as new color
    let finalColors = displayColors;
    if (minDistance > 100) {
      const backgroundColorIndex = patternData.colors.length;
      const newDisplayColors = [...patternData.colors, {
        index: backgroundColorIndex,
        rgb: selectedColor.rgb,
        hex: selectedColor.hex,
        name: selectedColor.name || 'Background'
      }];
      targetColorIndex = backgroundColorIndex;
      finalColors = newDisplayColors;
      setDisplayColors(newDisplayColors);
    }
    
    // Replace all unassigned (-1) areas with the target color
    for (let row = 0; row < newGrid.length; row++) {
      for (let col = 0; col < newGrid[row].length; col++) {
        if (newGrid[row][col] === -1) {
          newGrid[row][col] = targetColorIndex;
        }
      }
    }
    
    setEditedGrid(newGrid);
    setHasUnassignedPadding(false);
    
    // Regenerate scanlines with the updated grid and colors
    const newScanlines = regenerateScanlines(newGrid, finalColors);
    setUpdatedScanlines(newScanlines);
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.post('/api/generate-pdf', {
        pattern_data: { ...patternData, grid: editedGrid }
      }, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'crochet_pattern.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF download failed:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const renderGrid = () => {
    const colors = displayColors;
    const grid = editedGrid;
    const maxDisplayRows = grid.length;
    const maxDisplayCols = grid[0].length;
    
    return (
      <div className="pattern-grid">
        <h3>Pattern Grid</h3>
        <div className="grid-info">
          <p><strong>Pattern Size:</strong> {patternData.dimensions.width} × {patternData.dimensions.height} stitches</p>
          <p><strong>Finished Size:</strong> {patternData.gauge_info.pattern_dimensions.width_inches}" × {patternData.gauge_info.pattern_dimensions.height_inches}"</p>
          <p><strong>Difficulty:</strong> {patternData.difficulty}</p>
          <p><strong>Estimated Time:</strong> {patternData.estimated_time.hours} hours</p>
        </div>
        
        {hasUnassignedPadding && !editMode && patternData.initial_palette && (
          <div className="background-color-prompt" style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '16px',
            color: '#856404'
          }}>
            <div style={{ marginBottom: '12px' }}>
              <strong>⚠️ Background Color Needed:</strong> Your pattern has unassigned padding areas (marked with "?").
            </div>
            <div style={{ marginBottom: '8px', fontSize: '0.9rem' }}>
              <strong>Choose a background color from your image:</strong>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => handleBackgroundColorChange(undefined)}
                disabled={regeneratingPattern}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  border: backgroundColorIndex === undefined ? '2px solid #1976d2' : '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: backgroundColorIndex === undefined ? '#e3f2fd' : 'white',
                  cursor: regeneratingPattern ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '3px'
                }} />
                White (default)
              </button>
              {patternData.initial_palette.map((color, index) => (
                <button
                  key={index}
                  onClick={() => handleBackgroundColorChange(color.index)}
                  disabled={regeneratingPattern}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    border: backgroundColorIndex === color.index ? '2px solid #1976d2' : '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: backgroundColorIndex === color.index ? '#e3f2fd' : 'white',
                    cursor: regeneratingPattern ? 'not-allowed' : 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: color.hex,
                    border: '1px solid #ccc',
                    borderRadius: '3px'
                  }} />
                  {color.name || `Color ${color.index + 1}`}
                </button>
              ))}
            </div>
          </div>
        )}
        
        <div className="grid-controls">
          <button 
            className={`edit-mode-btn ${editMode ? 'active' : ''}`}
            onClick={() => setEditMode(!editMode)}
          >
            {editMode ? 'Exit Edit Mode' : 'Edit Pattern'}
          </button>

          <div className="zoom-controls">
            <button 
              className="zoom-btn"
              onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.25))}
              disabled={zoomLevel <= 0.5}
            >
              -
            </button>
            <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
            <button 
              className="zoom-btn"
              onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.25))}
              disabled={zoomLevel >= 3}
            >
              +
            </button>
            <button 
              className="zoom-btn reset"
              onClick={() => setZoomLevel(1)}
            >
              Reset
            </button>
          </div>
        </div>

        {editMode && (
          <div className="edit-controls">
            <h4>Select Color to Paint:</h4>
            <div className="color-selector">
              {colors.map(color => (
                <button
                  key={color.index}
                  className={`color-selector-btn ${selectedColor === color.index ? 'selected' : ''}`}
                  style={{ backgroundColor: color.hex }}
                  onClick={() => setSelectedColor(color.index)}
                  title={`${color.name || `Color ${String.fromCharCode(65 + color.index)}`} - ${color.hex}`}
                >
                  {String.fromCharCode(65 + color.index)}
                </button>
              ))}
            </div>
            <p className="edit-instructions">
              Click on any stitch in the grid to change its color to the selected color.
            </p>
          </div>
        )}
        
        <div className="color-legend">
          <h4>Color Legend:</h4>
          {colors.map(color => (
            <div key={color.index} className="color-item">
              <div 
                className="color-square" 
                style={{ backgroundColor: color.hex }}
              ></div>
              <span>{String.fromCharCode(65 + color.index)} - {color.name || 'Unknown'} ({color.hex})</span>
            </div>
          ))}
        </div>
        
        <div className="grid-container full-grid">
          <div className="grid-wrapper" style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'top left' }}>
            <table className="stitch-grid">
              <tbody>
                {grid.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td className="row-number">{rowIndex + 1}</td>
                    {row.map((colorIndex, colIndex) => {
                      const isUnassigned = colorIndex === -1;
                      const cellStyle = isUnassigned 
                        ? { 
                            backgroundColor: '#f0f0f0', 
                            border: '2px dashed #ccc',
                            color: '#999'
                          }
                        : { backgroundColor: colors[colorIndex]?.hex };
                      
                      return (
                        <td 
                          key={colIndex}
                          className={`stitch-cell ${editMode ? 'editable' : ''} ${isUnassigned ? 'unassigned' : ''}`}
                          style={cellStyle}
                          title={isUnassigned 
                            ? `Row ${rowIndex + 1}, Col ${colIndex + 1}: Unassigned padding - Select background color in edit mode`
                            : `Row ${rowIndex + 1}, Col ${colIndex + 1}: ${String.fromCharCode(65 + colorIndex)}${editMode ? ' - Click to edit' : ''}`
                          }
                          onClick={() => !isUnassigned && handlePixelClick(rowIndex, colIndex)}
                        >
                          {isUnassigned ? '?' : String.fromCharCode(65 + colorIndex)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderMaterials = () => {
    const { yarn_requirements, gauge_info } = patternData;
    
    return (
      <div className="materials-section">
        <h3>Materials & Gauge</h3>
        
        <div className="gauge-info">
          <h4>Gauge Information:</h4>
          <ul>
            <li>Gauge: {gauge_info.stitches_per_4_inches} stitches = 4 inches</li>
            <li>Finished Size: {gauge_info.pattern_dimensions.width_inches}" × {gauge_info.pattern_dimensions.height_inches}"</li>
            <li>Recommended Hooks: {gauge_info.recommended_hook_sizes.join(', ')}</li>
          </ul>
        </div>
        
        <div className="yarn-requirements">
          <h4>Yarn Requirements:</h4>
          <table className="materials-table">
            <thead>
              <tr>
                <th>Color</th>
                <th>Stitches</th>
                <th>Yards</th>
                <th>Skeins</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(yarn_requirements.by_color).map(([colorIndex, req]: [string, any]) => (
                <tr key={colorIndex}>
                  <td>
                    <div className="color-info">
                      <div 
                        className="color-square" 
                        style={{ backgroundColor: req.color_info.hex }}
                      ></div>
                      {String.fromCharCode(65 + parseInt(colorIndex))} ({req.color_info.name || 'Unknown'})
                    </div>
                  </td>
                  <td>{req.stitches}</td>
                  <td>{req.total_yards}</td>
                  <td>{req.skeins}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td><strong>TOTAL</strong></td>
                <td><strong>{yarn_requirements.total_stitches}</strong></td>
                <td><strong>{yarn_requirements.total_yards}</strong></td>
                <td><strong>{Object.values(yarn_requirements.by_color).reduce((sum: number, req: any) => sum + req.skeins, 0)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderInstructions = () => {
    const scanlines = updatedScanlines;
    const totalRows = scanlines.length;
    const completedCount = completedRows.size;
    const progressPercentage = totalRows > 0 ? Math.round((completedCount / totalRows) * 100) : 0;
    
    return (
      <div className="instructions-section">
        <h3>Row-by-Row Instructions</h3>
        
        <div className="progress-summary">
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <span className="progress-text">
              {completedCount} of {totalRows} rows completed ({progressPercentage}%)
            </span>
          </div>
          <div className="progress-actions">
            <button 
              className="mark-all-btn"
              onClick={() => setCompletedRows(new Set(scanlines.map(s => s.row)))}
            >
              Mark All Complete
            </button>
            <button 
              className="clear-all-btn"
              onClick={() => setCompletedRows(new Set())}
            >
              Clear All
            </button>
          </div>
        </div>
        
        <p className="instructions-intro">
          Work in single crochet throughout. Change colors as indicated. Check off rows as you complete them:
        </p>
        
        <div className="scanlines">
          {scanlines.map(scanline => (
            <div key={scanline.row} className={`scanline ${completedRows.has(scanline.row) ? 'completed' : ''}`}>
              <label className="row-checkbox">
                <input 
                  type="checkbox" 
                  checked={completedRows.has(scanline.row)}
                  onChange={() => handleRowCompletion(scanline.row)}
                />
                <span className="checkmark"></span>
              </label>
              <span className="row-number">Row {scanline.row}</span>
              <span className="direction">({scanline.direction})</span>:
              <span className="instructions">
                {scanline.instructions.map((inst: any, index: number) => (
                  <span key={index} className="instruction-group">
                    {index > 0 && ', '}
                    {inst.count} <span style={{ color: inst.color_hex }}>
                      {inst.color_name || String.fromCharCode(65 + inst.color_index)}
                    </span>
                  </span>
                ))}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="pattern-preview">
      <div className="preview-header">
        <h2>Generated Pattern</h2>
        <button 
          className="download-btn"
          onClick={handleDownloadPDF}
          disabled={downloading}
        >
          {downloading ? 'Generating PDF...' : 'Download PDF Pattern'}
        </button>
      </div>
      
      <div className="tab-navigation">
        <button 
          className={`tab ${activeTab === 'grid' ? 'active' : ''}`}
          onClick={() => setActiveTab('grid')}
        >
          Pattern Grid
        </button>
        <button 
          className={`tab ${activeTab === 'materials' ? 'active' : ''}`}
          onClick={() => setActiveTab('materials')}
        >
          Materials
        </button>
        <button 
          className={`tab ${activeTab === 'instructions' ? 'active' : ''}`}
          onClick={() => setActiveTab('instructions')}
        >
          Instructions
        </button>
      </div>
      
      <div className="tab-content">
        {activeTab === 'grid' && renderGrid()}
        {activeTab === 'materials' && renderMaterials()}
        {activeTab === 'instructions' && renderInstructions()}
      </div>
    </div>
  );
};

export default PatternPreview;